import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send } from 'lucide-react';
import { useFollowups } from '../hooks/useFollowups';
import { MAX_DRAFT, checkDraft, checkSettings, contextLine, followupStatus, kindLabel } from '../utils/followups';
import { ago } from '../utils/botQuestions';

const card = 'bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3';
const input = 'bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading';

function Pending({ f, onApprove, onDiscard }) {
  const [text, setText] = useState(f.draft || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const problem = checkDraft(text);
    if (problem) { setError(problem); return; }
    setError('');
    setSaving(true);
    await onApprove(f.id, text);
    setSaving(false);
  };
  return (
    <form onSubmit={submit} aria-label={`Seguimiento a ${f.customer_name || f.phone}`} className="border border-seryi-bg-light rounded-2xl p-4 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-seryi-text">
        <span><strong className="text-seryi-heading">{f.customer_name || 'Cliente'}</strong> · {f.phone} · {ago(f.created_at)}</span>
        <span className="text-fuchsia-300">{kindLabel(f)}</span>
      </div>
      <p className="text-[11px] text-seryi-text">{contextLine(f)}</p>
      <label htmlFor={`fu-text-${f.id}`} className="sr-only">Mensaje para {f.customer_name || f.phone}</label>
      <textarea id={`fu-text-${f.id}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={MAX_DRAFT} rows={3} className={`${input} w-full`} />
      <p className="text-[10px] text-seryi-text">Al enviarlo se le agrega «Si prefieres que no te escribamos más, responde NO.»</p>
      <div className="flex justify-end gap-2">
        <button type="button" disabled={saving} onClick={() => onDiscard(f.id)} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl disabled:opacity-50">Descartar</button>
        <button type="submit" disabled={saving} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-50">Aprobar y enviar</button>
      </div>
      {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
    </form>
  );
}

function Num({ id, label, value, onChange, min, max, suffix }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] text-seryi-text mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input id={id} type="number" inputMode="numeric" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)} className={`${input} w-20`} />
        {suffix && <span className="text-[11px] text-seryi-text">{suffix}</span>}
      </div>
    </div>
  );
}

export default function FollowupsPage() {
  const { settings, items, loading, missing, saveSettings, approve, discard } = useFollowups();
  const [form, setForm] = useState(settings);
  const [error, setError] = useState('');
  useEffect(() => { setForm(settings); }, [settings]);

  if (missing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261018a_followups.sql</code> en Supabase para usar el seguimiento automático.</div>;
  }

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const save = async (e) => {
    e.preventDefault();
    const problem = checkSettings(form);
    if (problem) { setError(problem); return; }
    setError('');
    await saveSettings(form);
  };
  const pending = items.filter((f) => f.status === 'por_aprobar');
  const queued = items.filter((f) => ['redactar', 'redactando', 'aprobado', 'enviando'].includes(f.status));
  const history = items.filter((f) => ['enviado', 'error', 'descartado', 'cancelado'].includes(f.status)).slice(0, 30);

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><Send size={20} /> Seguimiento automático</h2>
        <p className="text-xs text-seryi-text">Keyla detecta a quién conviene escribirle (un lead que se enfrió, una cotización sin respuesta, un cliente que no volvió), la IA redacta un mensaje corto con lo que se hablaron y tú lo apruebas antes de que salga. Sale por el WhatsApp del negocio, de lunes a sábado de 8 am a 8 pm, con un tope diario. Si el cliente escribe o responde NO, se detiene solo.</p>
      </div>

      {loading ? <p className="text-xs text-seryi-text">Cargando…</p> : (
        <>
          <form onSubmit={save} noValidate aria-label="Ajustes" className={card}>
            <label className="flex items-center gap-2 text-xs font-bold text-seryi-heading">
              <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
              Encender el seguimiento automático
            </label>
            <label className="flex items-center gap-2 text-xs text-seryi-heading">
              <input type="checkbox" checked={form.require_approval} onChange={(e) => set('require_approval', e.target.checked)} />
              Pedirme aprobación antes de enviar cada mensaje (recomendado)
            </label>
            {!form.require_approval && form.enabled && <p role="alert" className="text-[11px] text-amber-300">Sin aprobación, los mensajes salen solos a tus clientes. Empieza con aprobación y revisa cómo escribe la IA.</p>}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-seryi-heading"><input type="checkbox" checked={form.rule_lead} onChange={(e) => set('rule_lead', e.target.checked)} /> Leads que se enfriaron</label>
                <Num id="fu-lead" label="Después de" value={form.lead_after_hours} onChange={(v) => set('lead_after_hours', v)} min={4} max={168} suffix="horas sin hablar" />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-seryi-heading"><input type="checkbox" checked={form.rule_quote} onChange={(e) => set('rule_quote', e.target.checked)} /> Cotizaciones sin respuesta</label>
                <Num id="fu-quote" label="Después de" value={form.quote_after_days} onChange={(v) => set('quote_after_days', v)} min={1} max={14} suffix="días" />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-seryi-heading"><input type="checkbox" checked={form.rule_rebook} onChange={(e) => set('rule_rebook', e.target.checked)} /> Invitar a volver a agendar</label>
                <Num id="fu-rebook" label="Después de" value={form.rebook_after_days} onChange={(v) => set('rebook_after_days', v)} min={7} max={365} suffix="días de su cita" />
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <Num id="fu-touches" label="Mensajes por caso (máx.)" value={form.max_touches} onChange={(v) => set('max_touches', v)} min={1} max={3} />
              <Num id="fu-cap" label="Tope de mensajes por día" value={form.daily_cap} onChange={(v) => set('daily_cap', v)} min={1} max={100} />
            </div>
            <p className="text-[11px] text-seryi-text">Nunca se le escribe a quien escribió después, a quien respondió NO, a chats silenciados ni con el bot pausado. Con el modo prueba encendido solo se escribe a tus números de prueba.</p>
            <button type="submit" className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl">Guardar ajustes</button>
            {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
          </form>

          <section aria-label="Por aprobar" className={card}>
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Por aprobar {pending.length > 0 && <span className="text-fuchsia-300">({pending.length})</span>}</h3>
            {pending.length === 0 ? <p className="text-xs text-seryi-text">No hay mensajes por aprobar. Cuando Keyla detecte un caso, aparece aquí con el texto ya redactado.</p> : pending.map((f) => <Pending key={f.id} f={f} onApprove={approve} onDiscard={discard} />)}
          </section>

          {queued.length > 0 && (
            <section aria-label="En cola" className={card}>
              <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">En cola ({queued.length})</h3>
              {queued.map((f) => (
                <p key={f.id} className="text-xs text-seryi-heading border-t border-seryi-bg-light pt-2">
                  <strong>{f.customer_name || f.phone}</strong> · {kindLabel(f)} · <span className={followupStatus(f).tone}>{followupStatus(f).label}</span>
                </p>
              ))}
            </section>
          )}

          <section aria-label="Historial" className={card}>
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Historial</h3>
            {history.length === 0 ? <p className="text-xs text-seryi-text">Todavía no se ha enviado ningún seguimiento.</p> : history.map((f) => {
              const info = followupStatus(f);
              return (
                <details key={f.id} className="border-t border-seryi-bg-light pt-2 text-xs">
                  <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-2 text-seryi-heading">
                    <span><strong>{f.customer_name || f.phone}</strong> · {kindLabel(f)} · {ago(f.sent_at || f.created_at)}</span>
                    <span className={info.tone}>{info.label}</span>
                  </summary>
                  {f.error && <p className="text-rose-300 mt-1">{f.error}</p>}
                  <p className="text-seryi-text mt-1">{contextLine(f)}</p>
                  {f.draft && <p className="whitespace-pre-line text-seryi-text mt-1">«{f.draft}»</p>}
                </details>
              );
            })}
          </section>
          <p className="text-[11px] text-seryi-text">Quien responde se atiende en el chat de siempre. Las preguntas que el bot no sabe contestar están en <Link to="/v2/preguntas-bot" className="text-seryi-green hover:underline">Preguntas del bot</Link>.</p>
        </>
      )}
    </div>
  );
}
